import { TOKEN_NAME } from "@/services/api";

/**
 * Quota-safe localStorage writes.
 *
 * Heavy/long-lived users accumulate enough cached data (smart-routine state,
 * watch history, analytics queues, …) to exhaust the ~5MB localStorage quota.
 * Once full, every `setItem` throws a QuotaExceededError. Unguarded throws
 * crash rendering (no error boundary) and break the auth-token write, which is
 * the root cause of the "blank page on sign-in / payment / confirmation"
 * reports.
 *
 * `safeSetItem` never throws. On quota exhaustion it evicts cached entries
 * LIFO (most-recently-written first) and retries, but never evicts the auth
 * token — losing the token is what actually logs users out.
 */

/**
 * A token is evictable only when it is *provably* invalid: blank, or a
 * well-formed JWT whose `exp` has already passed. A token received from an API
 * response is always valid and freshest, so an opaque/unparseable token is
 * treated as valid (not evictable) — we only drop one we can prove is useless.
 */
function isEvictableToken(value: string | null): boolean {
  if (!value) return true; // blank/absent → useless, safe to evict
  const parts = value.split(".");
  if (parts.length === 3) {
    try {
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
      );
      if (typeof payload.exp === "number" && payload.exp * 1000 <= Date.now()) {
        return true; // provably expired
      }
    } catch {
      // Unparseable payload → not provably invalid → keep it.
    }
  }
  return false;
}

/**
 * Keys that must not be evicted to make room for another write. The auth
 * token is protected unless it is provably invalid (see isEvictableToken).
 */
function isProtected(key: string): boolean {
  if (key === TOKEN_NAME || /token/i.test(key)) {
    return !isEvictableToken(localStorage.getItem(key));
  }
  return false;
}

function isQuotaError(e: unknown): boolean {
  if (!(e instanceof DOMException)) return false;
  // Older Safari/Firefox don't set the modern name, so fall back to legacy codes.
  const legacyCode = (e as { code?: number }).code;
  return (
    e.name === "QuotaExceededError" ||
    e.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    legacyCode === 22 ||
    legacyCode === 1014
  );
}

/**
 * The newest (last-inserted) key that is safe to evict, or null if none.
 * localStorage preserves insertion order, so the highest index is the most
 * recently written entry — that is the LIFO eviction candidate.
 */
function newestEvictableKey(currentKey: string): string | null {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const k = localStorage.key(i);
    if (k === null || k === currentKey || isProtected(k)) continue;
    return k;
  }
  return null;
}

/**
 * Write to localStorage without ever throwing.
 * @returns true if the value was stored, false otherwise.
 */
export function safeSetItem(key: string, value: string): boolean {
  if (typeof window === "undefined") return false;

  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    if (!isQuotaError(e)) return false;
  }

  // Quota hit: evict LIFO and retry until it fits or nothing is left to evict.
  while (true) {
    const victim = newestEvictableKey(key);
    if (victim === null) return false;
    localStorage.removeItem(victim);
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      if (!isQuotaError(e)) return false;
    }
  }
}
